import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SparklesIcon, SendIcon, ListIcon, MessageSquareIcon, ArrowLeftIcon, TargetIcon, UsersIcon, LayoutIcon, BrainIcon, ChevronDownIcon, CheckIcon, BoxIcon, WandIcon, MonitorIcon, SmartphoneIcon, UploadIcon, XIcon, PlusIcon, CheckCircleIcon } from 'lucide-react';
type DesignStyle = 'modern-soft' | 'material' | 'brutalist' | 'retro-terminal' | 'glass' | 'minimalist';
type ColorPalette = {
  id: string;
  name: string;
  primary: string;
  secondary: string;
  accent: string;
};
type GuidedPrompt = {
  purpose: string;
  targetUsers: string;
  interfaceLayout: string;
  keyFeatures: string;
  aiBehavior: string;
  exampleInteraction: string;
};
type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  showPreview?: boolean;
};
type Step = 'choice' | 'guided-form' | 'chat' | 'listing' | 'published';
const COLOR_PALETTES: ColorPalette[] = [{
  id: 'ocean-blue',
  name: 'Ocean Blue',
  primary: '#2563eb',
  secondary: '#06b6d4',
  accent: '#14b8a6'
}, {
  id: 'forest-green',
  name: 'Forest Green',
  primary: '#16a34a',
  secondary: '#10b981',
  accent: '#84cc16'
}, {
  id: 'sunset-orange',
  name: 'Sunset Orange',
  primary: '#ea580c',
  secondary: '#ef4444',
  accent: '#f59e0b'
}, {
  id: 'royal-purple',
  name: 'Royal Purple',
  primary: '#9333ea',
  secondary: '#6366f1',
  accent: '#a855f7'
}, {
  id: 'slate-gray',
  name: 'Slate Gray',
  primary: '#334155',
  secondary: '#475569',
  accent: '#71717a'
}, {
  id: 'rose-pink',
  name: 'Rose Pink',
  primary: '#e11d48',
  secondary: '#ec4899',
  accent: '#d946ef'
}, {
  id: 'amber-gold',
  name: 'Amber Gold',
  primary: '#d97706',
  secondary: '#eab308',
  accent: '#f97316'
}, {
  id: 'emerald-mint',
  name: 'Emerald Mint',
  primary: '#059669',
  secondary: '#4ade80',
  accent: '#2dd4bf'
}];
type StyleTemplate = {
  id: DesignStyle;
  name: string;
  description: string;
};
const STYLE_TEMPLATES: StyleTemplate[] = [{
  id: 'modern-soft',
  name: 'Modern Soft',
  description: 'Rounded, gentle, approachable'
}, {
  id: 'material',
  name: 'Material Design',
  description: 'Bold, elevated, structured'
}, {
  id: 'brutalist',
  name: 'Brutalist',
  description: 'Bold, raw, high-contrast'
}, {
  id: 'retro-terminal',
  name: 'Retro Terminal',
  description: 'Nostalgic, monospace, vintage'
}, {
  id: 'glass',
  name: 'Glass Morphism',
  description: 'Frosted, transparent, modern'
}, {
  id: 'minimalist',
  name: 'Minimalist',
  description: 'Clean, spacious, understated'
}];
const GuidedInput = ({
  icon: Icon,
  label,
  placeholder,
  value,
  onChange,
  multiline = false,
  accentColor
}: {
  icon: any;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  accentColor: string;
}) => <div className="space-y-2" data-id="element-1498">
    <label className="flex items-center space-x-2 text-sm font-medium text-gray-700" data-id="element-1499">
      <Icon className="h-4 w-4 text-gray-500" data-id="element-1500" />
      <span data-id="element-1501">{label}</span>
    </label>
    {multiline ? <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={3} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent transition-shadow" style={{
    '--tw-ring-color': accentColor
  } as React.CSSProperties} data-id="element-1502" /> : <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent transition-shadow" style={{
    '--tw-ring-color': accentColor
  } as React.CSSProperties} data-id="element-1503" />}
  </div>;
export const Create = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('choice');
  const [selectedStyle, setSelectedStyle] = useState<DesignStyle>('modern-soft');
  const [selectedPalette, setSelectedPalette] = useState<string>('ocean-blue');
  const [guidedPrompt, setGuidedPrompt] = useState<GuidedPrompt>({
    purpose: '',
    targetUsers: '',
    interfaceLayout: '',
    keyFeatures: '',
    aiBehavior: '',
    exampleInteraction: ''
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showStyleDropdown, setShowStyleDropdown] = useState(false);
  const [showPaletteDropdown, setShowPaletteDropdown] = useState(false);
  const [listingData, setListingData] = useState({
    title: '',
    description: '',
    detailedDescription: '',
    author: '',
    category: 'Productivity',
    price: 'free',
    features: [''],
    screenshots: [] as string[]
  });
  const styleDropdownRef = useRef<HTMLDivElement>(null);
  const paletteDropdownRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentTemplate = STYLE_TEMPLATES.find(t => t.id === selectedStyle)!;
  const currentPalette = COLOR_PALETTES.find(p => p.id === selectedPalette)!;
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (styleDropdownRef.current && !styleDropdownRef.current.contains(e.target as Node)) {
        setShowStyleDropdown(false);
      }
      if (paletteDropdownRef.current && !paletteDropdownRef.current.contains(e.target as Node)) {
        setShowPaletteDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [messages, isGenerating]);
  const handleStartFreeform = () => {
    setMessages([{
      id: Date.now().toString(),
      role: 'assistant',
      content: "Hi! I'm your AI app designer. What kind of app would you like to build today? You can describe it naturally, and we'll figure out the details together.",
      timestamp: new Date()
    }]);
    setStep('chat');
  };
  const generateCombinedPrompt = (): string => {
    const parts: string[] = [];
    if (guidedPrompt.purpose) parts.push(`Create an app that ${guidedPrompt.purpose}`);
    if (guidedPrompt.targetUsers) parts.push(`This app is designed for ${guidedPrompt.targetUsers}`);
    if (guidedPrompt.interfaceLayout) parts.push(`Interface: ${guidedPrompt.interfaceLayout}`);
    if (guidedPrompt.keyFeatures) parts.push(`Key features: ${guidedPrompt.keyFeatures}`);
    if (guidedPrompt.aiBehavior) parts.push(`AI behavior: ${guidedPrompt.aiBehavior}`);
    if (guidedPrompt.exampleInteraction) parts.push(`Example interaction: ${guidedPrompt.exampleInteraction}`);
    return parts.join('. ');
  };
  const handleGuidedSubmit = () => {
    const combinedPrompt = generateCombinedPrompt();
    if (!combinedPrompt.trim()) return;
    const initialUserMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: combinedPrompt,
      timestamp: new Date()
    };
    setMessages([initialUserMessage]);
    setStep('chat');
    simulateAIResponse(combinedPrompt);
  };
  const handleSendMessage = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!chatInput.trim() || isGenerating) return;
    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: chatInput,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    setChatInput('');
    simulateAIResponse(chatInput);
  };
  const simulateAIResponse = (userText: string) => {
    setIsGenerating(true);
    setTimeout(() => {
      const isFirstResponse = messages.length <= 1;
      const aiMsg: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: isFirstResponse ? "Great! I've created the initial structure for your app based on your description. I've set up the main layout and core components. What do you think? We can adjust the colors, add new features, or tweak the layout." : "I've updated the app with your requested changes. Let me know if you'd like to tweak anything else!",
        timestamp: new Date(),
        showPreview: true
      };
      setMessages(prev => [...prev, aiMsg]);
      setIsGenerating(false);
    }, 2000);
  };
  const updateGuidedField = (field: keyof GuidedPrompt) => (value: string) => {
    setGuidedPrompt(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newScreenshots = Array.from(files).map(file => URL.createObjectURL(file));
      setListingData(prev => ({
        ...prev,
        screenshots: [...prev.screenshots, ...newScreenshots]
      }));
    }
  };
  const removeScreenshot = (index: number) => {
    setListingData(prev => ({
      ...prev,
      screenshots: prev.screenshots.filter((_, i) => i !== index)
    }));
  };
  const addFeature = () => {
    setListingData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };
  const updateFeature = (index: number, value: string) => {
    setListingData(prev => {
      const newFeatures = [...prev.features];
      newFeatures[index] = value;
      return {
        ...prev,
        features: newFeatures
      };
    });
  };
  const removeFeature = (index: number) => {
    setListingData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };
  // --- Render Choice Step ---
  if (step === 'choice') {
    return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6" data-id="element-1504">
        <div className="max-w-4xl w-full" data-id="element-1505">
          <div className="text-center mb-12" data-id="element-1506">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg" data-id="element-1507">
              <SparklesIcon className="h-8 w-8 text-white" data-id="element-1508" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1509">
              How would you like to build?
            </h1>
            <p className="text-xl text-gray-600" data-id="element-1510">
              Choose your preferred way to start creating your app.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-1511">
            {/* Guided Card */}
            <button onClick={() => setStep('guided-form')} className="group bg-white p-8 rounded-3xl border-2 border-gray-200 hover:border-indigo-500 hover:shadow-xl transition-all text-left flex flex-col h-full" data-id="element-1512">
              <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-1513">
                <ListIcon className="h-7 w-7 text-indigo-600" data-id="element-1514" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1515">
                Guided Creation
              </h2>
              <p className="text-gray-600 flex-1 text-lg leading-relaxed" data-id="element-1516">
                Answer a few questions to build your app step-by-step. Perfect
                if you want a structured approach to defining your app's
                features and layout.
              </p>
            </button>

            {/* Freeform Card */}
            <button onClick={handleStartFreeform} className="group bg-white p-8 rounded-3xl border-2 border-gray-200 hover:border-purple-500 hover:shadow-xl transition-all text-left flex flex-col h-full" data-id="element-1517">
              <div className="w-14 h-14 bg-purple-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-1518">
                <MessageSquareIcon className="h-7 w-7 text-purple-600" data-id="element-1519" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1520">
                Freeform Chat
              </h2>
              <p className="text-gray-600 flex-1 text-lg leading-relaxed" data-id="element-1521">
                Describe your app naturally in a conversation. Best if you have
                a clear vision and want to jump straight into building with our
                AI assistant.
              </p>
            </button>
          </div>
        </div>
      </div>;
  }
  // --- Render Guided Form Step ---
  if (step === 'guided-form') {
    return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-1522">
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center sticky top-0 z-10" data-id="element-1523">
          <button onClick={() => setStep('choice')} className="flex items-center text-gray-500 hover:text-gray-900 transition-colors font-medium" data-id="element-1524">
            <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-1525" />
            Back
          </button>
          <div className="mx-auto flex items-center space-x-2" data-id="element-1526">
            <ListIcon className="h-5 w-5 text-indigo-600" data-id="element-1527" />
            <h1 className="text-lg font-bold text-gray-900" data-id="element-1528">Guided Setup</h1>
          </div>
          <div className="w-20" data-id="element-1529" /> {/* Spacer for centering */}
        </div>

        <div className="flex-1 overflow-y-auto p-6 md:p-10" data-id="element-1530">
          <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-sm border border-gray-200 p-8 md:p-10" data-id="element-1531">
            <div className="mb-10" data-id="element-1532">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1533">
                Define Your App
              </h2>
              <p className="text-gray-600" data-id="element-1534">
                Fill in the details below to generate your initial app
                structure.
              </p>
            </div>

            <div className="space-y-8" data-id="element-1535">
              <GuidedInput icon={TargetIcon} label="What does your app do?" placeholder="e.g. Helps users manage their daily tasks and stay organized..." value={guidedPrompt.purpose} onChange={updateGuidedField('purpose')} multiline accentColor={currentPalette.primary} data-id="element-1536" />

              <GuidedInput icon={UsersIcon} label="Who is this app for?" placeholder="e.g. Busy professionals, students..." value={guidedPrompt.targetUsers} onChange={updateGuidedField('targetUsers')} accentColor={currentPalette.primary} data-id="element-1537" />

              <GuidedInput icon={LayoutIcon} label="How should it look?" placeholder="e.g. Clean sidebar navigation, main content area with task list..." value={guidedPrompt.interfaceLayout} onChange={updateGuidedField('interfaceLayout')} multiline accentColor={currentPalette.primary} data-id="element-1538" />

              <GuidedInput icon={ListIcon} label="What are the key features?" placeholder="e.g. Add/edit/delete tasks, due dates, priority levels..." value={guidedPrompt.keyFeatures} onChange={updateGuidedField('keyFeatures')} multiline accentColor={currentPalette.primary} data-id="element-1539" />

              <GuidedInput icon={BrainIcon} label="How should the AI behave?" placeholder="e.g. Be encouraging and supportive, suggest task breakdowns..." value={guidedPrompt.aiBehavior} onChange={updateGuidedField('aiBehavior')} multiline accentColor={currentPalette.primary} data-id="element-1540" />

              <GuidedInput icon={MessageSquareIcon} label="Example interaction (optional)" placeholder="e.g. User: 'I need to finish my report' → AI: 'Let's break that down...'" value={guidedPrompt.exampleInteraction} onChange={updateGuidedField('exampleInteraction')} multiline accentColor={currentPalette.primary} data-id="element-1541" />

              {/* Design Selectors */}
              <div className="pt-6 border-t border-gray-100 grid md:grid-cols-2 gap-6" data-id="element-1542">
                {/* Style Dropdown */}
                <div ref={styleDropdownRef} className="relative" data-id="element-1543">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1544">
                    Design Style
                  </label>
                  <button onClick={() => {
                  setShowStyleDropdown(!showStyleDropdown);
                  setShowPaletteDropdown(false);
                }} className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1545">
                    <div className="flex items-center space-x-3" data-id="element-1546">
                      <LayoutIcon className="h-5 w-5 text-gray-400" data-id="element-1547" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-1548">
                        {currentTemplate.name}
                      </span>
                    </div>
                    <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${showStyleDropdown ? 'rotate-180' : ''}`} data-id="element-1549" />
                  </button>

                  {showStyleDropdown && <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto" data-id="element-1550">
                      {STYLE_TEMPLATES.map(style => <button key={style.id} onClick={() => {
                    setSelectedStyle(style.id);
                    setShowStyleDropdown(false);
                  }} className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors ${selectedStyle === style.id ? 'bg-indigo-50/50' : ''}`} data-id="element-1551">
                          <div className="text-left" data-id="element-1552">
                            <div className="text-sm font-medium text-gray-900" data-id="element-1553">
                              {style.name}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5" data-id="element-1554">
                              {style.description}
                            </div>
                          </div>
                          {selectedStyle === style.id && <CheckIcon className="h-5 w-5 text-indigo-600" data-id="element-1555" />}
                        </button>)}
                    </div>}
                </div>

                {/* Palette Dropdown */}
                <div ref={paletteDropdownRef} className="relative" data-id="element-1556">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1557">
                    Color Palette
                  </label>
                  <button onClick={() => {
                  setShowPaletteDropdown(!showPaletteDropdown);
                  setShowStyleDropdown(false);
                }} className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-1558">
                    <div className="flex items-center space-x-3" data-id="element-1559">
                      <div className="flex space-x-1.5" data-id="element-1560">
                        <div className="w-5 h-5 rounded-md shadow-sm" style={{
                        backgroundColor: currentPalette.primary
                      }} data-id="element-1561" />
                        <div className="w-5 h-5 rounded-md shadow-sm" style={{
                        backgroundColor: currentPalette.secondary
                      }} data-id="element-1562" />
                        <div className="w-5 h-5 rounded-md shadow-sm" style={{
                        backgroundColor: currentPalette.accent
                      }} data-id="element-1563" />
                      </div>
                      <span className="text-sm font-medium text-gray-900" data-id="element-1564">
                        {currentPalette.name}
                      </span>
                    </div>
                    <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${showPaletteDropdown ? 'rotate-180' : ''}`} data-id="element-1565" />
                  </button>

                  {showPaletteDropdown && <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto" data-id="element-1566">
                      {COLOR_PALETTES.map(palette => <button key={palette.id} onClick={() => {
                    setSelectedPalette(palette.id);
                    setShowPaletteDropdown(false);
                  }} className={`w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors ${selectedPalette === palette.id ? 'bg-indigo-50/50' : ''}`} data-id="element-1567">
                          <div className="flex items-center space-x-3" data-id="element-1568">
                            <div className="flex space-x-1.5" data-id="element-1569">
                              <div className="w-5 h-5 rounded-md shadow-sm" style={{
                          backgroundColor: palette.primary
                        }} data-id="element-1570" />
                              <div className="w-5 h-5 rounded-md shadow-sm" style={{
                          backgroundColor: palette.secondary
                        }} data-id="element-1571" />
                              <div className="w-5 h-5 rounded-md shadow-sm" style={{
                          backgroundColor: palette.accent
                        }} data-id="element-1572" />
                            </div>
                            <span className="text-sm font-medium text-gray-900" data-id="element-1573">
                              {palette.name}
                            </span>
                          </div>
                          {selectedPalette === palette.id && <CheckIcon className="h-5 w-5 text-indigo-600" data-id="element-1574" />}
                        </button>)}
                    </div>}
                </div>
              </div>

              <div className="pt-8" data-id="element-1575">
                <button onClick={handleGuidedSubmit} disabled={!Object.values(guidedPrompt).some(v => v.trim().length > 0)} className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg flex items-center justify-center space-x-2" data-id="element-1576">
                  <WandIcon className="h-5 w-5" data-id="element-1577" />
                  <span data-id="element-1578">Generate App</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // --- Render Listing Step ---
  if (step === 'listing') {
    return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-1579">
        <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center sticky top-0 z-10" data-id="element-1580">
          <button onClick={() => setStep('chat')} className="flex items-center text-gray-500 hover:text-gray-900 transition-colors font-medium" data-id="element-1581">
            <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-1582" />
            Back to Chat
          </button>
          <div className="mx-auto flex items-center space-x-2" data-id="element-1583">
            <BoxIcon className="h-5 w-5 text-indigo-600" data-id="element-1584" />
            <h1 className="text-lg font-bold text-gray-900" data-id="element-1585">Store Listing</h1>
          </div>
          <div className="w-24" data-id="element-1586" /> {/* Spacer for centering */}
        </div>

        <div className="flex-1 overflow-y-auto p-6 md:p-10" data-id="element-1587">
          <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-sm border border-gray-200 p-8 md:p-10" data-id="element-1588">
            <div className="mb-10" data-id="element-1589">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1590">
                Prepare Your Store Listing
              </h2>
              <p className="text-gray-600" data-id="element-1591">
                Fill in the details for how your app appears in the store.
              </p>
            </div>

            <div className="space-y-8" data-id="element-1592">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1593">
                <div className="space-y-2" data-id="element-1594">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-1595">
                    App Name
                  </label>
                  <input type="text" value={listingData.title} onChange={e => setListingData({
                  ...listingData,
                  title: e.target.value
                })} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow" placeholder="e.g. TaskMaster Pro" data-id="element-1596" />
                </div>
                <div className="space-y-2" data-id="element-1597">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-1598">
                    Author Name
                  </label>
                  <input type="text" value={listingData.author} onChange={e => setListingData({
                  ...listingData,
                  author: e.target.value
                })} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow" placeholder="Your name or company" data-id="element-1599" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1600">
                <div className="space-y-2" data-id="element-1601">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-1602">
                    Category
                  </label>
                  <select value={listingData.category} onChange={e => setListingData({
                  ...listingData,
                  category: e.target.value
                })} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow bg-white" data-id="element-1603">
                    {['Productivity', 'Creative', 'Chat', 'Code', 'Image', 'Writing', 'Lifestyle', 'Education', 'Business', 'Other'].map(cat => <option key={cat} value={cat} data-id="element-1604">
                        {cat}
                      </option>)}
                  </select>
                </div>
                <div className="space-y-2" data-id="element-1605">
                  <label className="block text-sm font-medium text-gray-700" data-id="element-1606">
                    Price
                  </label>
                  <select value={listingData.price} onChange={e => setListingData({
                  ...listingData,
                  price: e.target.value
                })} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow bg-white" data-id="element-1607">
                    <option value="free" data-id="element-1608">Free</option>
                    <option value="0.99" data-id="element-1609">$0.99</option>
                    <option value="1.99" data-id="element-1610">$1.99</option>
                    <option value="4.99" data-id="element-1611">$4.99</option>
                    <option value="9.99" data-id="element-1612">$9.99</option>
                    <option value="19.99" data-id="element-1613">$19.99</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2" data-id="element-1614">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1615">
                  Short Description
                </label>
                <textarea value={listingData.description} onChange={e => setListingData({
                ...listingData,
                description: e.target.value
              })} rows={2} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow" placeholder="A brief summary of what your app does..." data-id="element-1616" />
              </div>

              <div className="space-y-2" data-id="element-1617">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1618">
                  Detailed Description{' '}
                  <span className="text-gray-400 font-normal" data-id="element-1619">(Optional)</span>
                </label>
                <textarea value={listingData.detailedDescription} onChange={e => setListingData({
                ...listingData,
                detailedDescription: e.target.value
              })} rows={4} className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow" placeholder="Provide more details about features, use cases, etc." data-id="element-1620" />
              </div>

              {/* Key Features */}
              <div className="space-y-3" data-id="element-1621">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1622">
                  Key Features
                </label>
                {listingData.features.map((feature, index) => <div key={index} className="flex items-center space-x-2" data-id="element-1623">
                    <input type="text" value={feature} onChange={e => updateFeature(index, e.target.value)} className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow" placeholder="e.g. AI-powered task prioritization" data-id="element-1624" />
                    {listingData.features.length > 1 && <button onClick={() => removeFeature(index)} className="p-3 text-red-500 hover:bg-red-50 rounded-xl transition-colors" data-id="element-1625">
                        <XIcon className="h-5 w-5" data-id="element-1626" />
                      </button>}
                  </div>)}
                <button onClick={addFeature} className="flex items-center text-indigo-600 hover:text-indigo-700 text-sm font-medium mt-2" data-id="element-1627">
                  <PlusIcon className="h-4 w-4 mr-1" data-id="element-1628" />
                  Add Feature
                </button>
              </div>

              {/* Screenshots */}
              <div className="space-y-3" data-id="element-1629">
                <label className="block text-sm font-medium text-gray-700" data-id="element-1630">
                  Screenshots
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-1631">
                  {listingData.screenshots.map((screenshot, index) => <div key={index} className="relative group" data-id="element-1632">
                      <img src={screenshot} alt={`Screenshot ${index + 1}`} className="w-full aspect-video object-cover rounded-xl border-2 border-gray-200" data-id="element-1633" />
                      <button onClick={() => removeScreenshot(index)} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" data-id="element-1634">
                        <XIcon className="h-4 w-4" data-id="element-1635" />
                      </button>
                    </div>)}
                  <label className="flex flex-col items-center justify-center aspect-video border-2 border-dashed border-gray-300 rounded-xl hover:border-indigo-500 hover:bg-indigo-50 cursor-pointer transition-all" data-id="element-1636">
                    <UploadIcon className="h-8 w-8 text-gray-400 mb-2" data-id="element-1637" />
                    <span className="text-sm text-gray-600 font-medium" data-id="element-1638">
                      Add Screenshot
                    </span>
                    <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" data-id="element-1639" />
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="pt-8 border-t border-gray-100 flex items-center justify-end space-x-4" data-id="element-1640">
                <button className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors" data-id="element-1641">
                  Save Draft
                </button>
                <button onClick={() => setStep('published')} disabled={!listingData.title || !listingData.description} className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl font-bold hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md" data-id="element-1642">
                  Publish to Store
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  // --- Render Published Step ---
  if (step === 'published') {
    return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6" data-id="element-1643">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-gray-200 p-10 text-center" data-id="element-1644">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-1645">
            <CheckCircleIcon className="h-10 w-10 text-green-600" data-id="element-1646" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1647">
            Your App is Live!
          </h1>
          <p className="text-gray-600 text-lg mb-8" data-id="element-1648">
            Your app has been published to the store and is now available for
            users to discover.
          </p>
          <div className="space-y-3" data-id="element-1649">
            <button onClick={() => navigate('/')} className="w-full bg-indigo-600 text-white py-3.5 rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-md" data-id="element-1650">
              View in Store
            </button>
            <button onClick={() => {
            setStep('choice');
            setListingData({
              title: '',
              description: '',
              detailedDescription: '',
              author: '',
              category: 'Productivity',
              price: 'free',
              features: [''],
              screenshots: []
            });
            setMessages([]);
            setGuidedPrompt({
              purpose: '',
              targetUsers: '',
              interfaceLayout: '',
              keyFeatures: '',
              aiBehavior: '',
              exampleInteraction: ''
            });
          }} className="w-full bg-white border border-gray-300 text-gray-700 py-3.5 rounded-xl font-bold hover:bg-gray-50 transition-colors" data-id="element-1651">
              Create Another
            </button>
          </div>
        </div>
      </div>;
  }
  // --- Render Chat Interface Step ---
  return <div className="h-screen flex flex-col bg-gray-50" data-id="element-1652">
      {/* Chat Top Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between shadow-sm z-10" data-id="element-1653">
        <div className="flex items-center space-x-4" data-id="element-1654">
          <button onClick={() => setStep('choice')} className="p-2 -ml-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Back to start" data-id="element-1655">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-1656" />
          </button>
          <div className="flex items-center space-x-3" data-id="element-1657">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shadow-sm" style={{
            background: `linear-gradient(135deg, ${currentPalette.primary}, ${currentPalette.secondary})`
          }} data-id="element-1658">
              <SparklesIcon className="h-5 w-5 text-white" data-id="element-1659" />
            </div>
            <div data-id="element-1660">
              <h1 className="text-sm font-bold text-gray-900" data-id="element-1661">App Designer</h1>
              <div className="flex items-center text-xs text-gray-500 space-x-2 mt-0.5" data-id="element-1662">
                <span className="flex items-center" data-id="element-1663">
                  <LayoutIcon className="h-3 w-3 mr-1" data-id="element-1664" /> {currentTemplate.name}
                </span>
                <span data-id="element-1665">•</span>
                <span className="flex items-center" data-id="element-1666">
                  <div className="w-2 h-2 rounded-full mr-1.5" style={{
                  backgroundColor: currentPalette.primary
                }} data-id="element-1667" />
                  {currentPalette.name}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-3" data-id="element-1668">
          <button onClick={() => setStep('listing')} className="px-5 py-2 text-white rounded-lg transition-colors text-sm font-medium shadow-sm bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700" data-id="element-1669">
            Publish
          </button>
        </div>
      </div>

      {/* Split Panel: Chat (Left) + Preview (Right) */}
      <div className="flex-1 flex overflow-hidden" data-id="element-1670">
        {/* Left Panel - Chat Messages */}
        <div className="w-full md:w-[45%] lg:w-[40%] flex flex-col bg-white border-r border-gray-200" data-id="element-1671">
          {/* Chat Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 scroll-smooth" data-id="element-1672">
            <div className="space-y-4" data-id="element-1673">
              {messages.map(msg => <div key={msg.id} className="flex flex-col" data-id="element-1674">
                  <div className={`rounded-2xl p-4 shadow-sm ${msg.role === 'user' ? 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-tr-sm ml-auto max-w-[85%]' : 'bg-gray-50 border border-gray-200 text-gray-800 rounded-tl-sm mr-auto max-w-[85%]'}`} data-id="element-1675">
                    <p className="text-sm whitespace-pre-wrap leading-relaxed" data-id="element-1676">
                      {msg.content}
                    </p>
                  </div>
                  <span className={`text-xs text-gray-400 mt-1.5 px-1 ${msg.role === 'user' ? 'text-right' : 'text-left'}`} data-id="element-1677">
                    {msg.timestamp.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
                  </span>
                </div>)}

              {/* Loading Indicator */}
              {isGenerating && <div className="flex justify-start" data-id="element-1678">
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl rounded-tl-sm p-4 shadow-sm" data-id="element-1679">
                    <div className="flex items-center space-x-2" data-id="element-1680">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" data-id="element-1681" />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                    animationDelay: '0.2s'
                  }} data-id="element-1682" />
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                    animationDelay: '0.4s'
                  }} data-id="element-1683" />
                    </div>
                  </div>
                </div>}
              <div ref={messagesEndRef} data-id="element-1684" />
            </div>
          </div>

          {/* Chat Input - Fixed at Bottom of Left Panel */}
          <div className="border-t border-gray-200 p-4 bg-white" data-id="element-1685">
            <form onSubmit={handleSendMessage} className="relative flex items-end" data-id="element-1686">
              <textarea value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }} placeholder="Describe changes or ask questions..." className="w-full border-2 border-gray-200 rounded-2xl pl-4 pr-14 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none text-sm shadow-sm bg-gray-50 focus:bg-white transition-colors" rows={1} style={{
              minHeight: '48px',
              maxHeight: '120px'
            }} disabled={isGenerating} data-id="element-1687" />
              <button type="submit" disabled={!chatInput.trim() || isGenerating} className="absolute right-2 bottom-2 p-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm" data-id="element-1688">
                <SendIcon className="h-4 w-4" data-id="element-1689" />
              </button>
            </form>
            <div className="text-center mt-2" data-id="element-1690">
              <span className="text-xs text-gray-400" data-id="element-1691">
                Press Enter to send, Shift + Enter for new line
              </span>
            </div>
          </div>
        </div>

        {/* Right Panel - UI Preview */}
        <div className="hidden md:flex flex-1 flex-col bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-1692">
          {/* Preview Toolbar */}
          <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between" data-id="element-1693">
            <div className="flex items-center space-x-2" data-id="element-1694">
              <button className="p-1.5 rounded text-gray-600 hover:bg-gray-100 transition-colors" data-id="element-1695">
                <MonitorIcon className="h-4 w-4" data-id="element-1696" />
              </button>
              <button className="p-1.5 rounded text-gray-400 hover:bg-gray-100 transition-colors" data-id="element-1697">
                <SmartphoneIcon className="h-4 w-4" data-id="element-1698" />
              </button>
            </div>
            <span className="text-xs font-medium text-gray-500" data-id="element-1699">
              Live Preview
            </span>
          </div>

          {/* Preview Content */}
          <div className="flex-1 overflow-auto p-6 flex items-center justify-center" data-id="element-1700">
            {messages.length > 0 ? <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-gray-200 p-8" data-id="element-1701">
                <div className="text-center" data-id="element-1702">
                  <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg" style={{
                background: `linear-gradient(135deg, ${currentPalette.primary}, ${currentPalette.secondary})`
              }} data-id="element-1703">
                    <WandIcon className="h-8 w-8 text-white" data-id="element-1704" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-1705">
                    Your App
                  </h3>
                  <p className="text-gray-600 mb-6" data-id="element-1706">
                    Preview with {currentTemplate.name} style
                  </p>
                  <div className="space-y-3" data-id="element-1707">
                    <input type="text" placeholder="Enter text..." className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" data-id="element-1708" />
                    <button className="w-full px-6 py-3 rounded-xl text-white font-semibold text-sm shadow-md hover:shadow-lg transition-shadow" style={{
                  background: `linear-gradient(135deg, ${currentPalette.primary}, ${currentPalette.secondary})`
                }} data-id="element-1709">
                      Primary Action
                    </button>
                  </div>
                </div>
              </div> : <div className="text-center max-w-md px-6" data-id="element-1710">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg" style={{
              background: `linear-gradient(135deg, ${currentPalette.primary}, ${currentPalette.secondary})`
            }} data-id="element-1711">
                  <WandIcon className="h-10 w-10 text-white" data-id="element-1712" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-1713">
                  Start Building
                </h3>
                <p className="text-gray-500 text-sm" data-id="element-1714">
                  Your app preview will appear here as you design
                </p>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};